import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  claimBrowserRecovery,
  classifyBrowserRecoveryError,
  clearBrowserRecoveryAttempt,
  clearChunkRecoveryCaches,
  BROWSER_RECOVERY_EVENT,
  hasBrowserRecoveryAttempted,
  markBrowserRecoverySuppressed,
  RECOVERY_QUERY_KEY,
  scheduleStableBrowserRecoveryReset,
} from "@/lib/browser-recovery";
import { sanitizeTelemetryText, sanitizeTelemetryUrl } from "@shared/client-error-telemetry";
import {
  getClientRuntimeDiagnostics,
  inferClientErrorAttribution,
} from "@/lib/client-error-diagnostics";

export { clearChunkRecoveryCaches } from "@/lib/browser-recovery";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  isOffline: boolean;
  recoverySuppressed: boolean;
}

/**
 * Top-level error boundary.
 *
 * Without a boundary, any uncaught render error (including a rejected `lazy()`
 * dynamic import) unmounts the whole React tree, leaving a blank white page.
 *
 * - Chunk-load errors trigger one cache-busted recovery per tab fault. The
 *   marker survives a document reload, including when sessionStorage is denied.
 * - Offline failures never auto-reload: the inline fallback tells the user
 *   exactly why the automatic recovery was suppressed.
 * - Any other error renders a visible Italian fallback (with a manual reload
 *   button) and reports only a sanitized category instead of failing silently.
 */
/**
 * Reload bypassing every cache layer. A plain `location.reload()` repeats the
 * SAME request: right after a deploy the Google Frontend edge can keep serving
 * the previous shell for minutes (`s-maxage` + `stale-while-revalidate`), so
 * the reload fetches the same stale HTML pointing at deleted chunks and the
 * user is stuck on this screen ("ricarica non fa nulla"). Changing the query
 * string changes the edge/browser cache key, guaranteeing a fresh shell.
 */
export function forceFreshReload(auto = false): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_cb", String(Date.now()));
    if (auto) url.searchParams.set(RECOVERY_QUERY_KEY, "1");
    else url.searchParams.delete(RECOVERY_QUERY_KEY);
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

function recoverFromChunkError(): boolean {
  if (!claimBrowserRecovery()) return false;
  void clearChunkRecoveryCaches().finally(() => forceFreshReload(true));
  return true;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
      isOffline: false,
      recoverySuppressed: false,
    };
  }

  private handlePreloadFailure = (event: Event) => {
    const detail = (event as CustomEvent<{ offline?: boolean }>).detail;
    this.setState({
      hasError: true,
      isChunkError: true,
      isOffline: detail?.offline === true,
      recoverySuppressed: true,
    });
  };

  componentDidMount() {
    window.addEventListener(BROWSER_RECOVERY_EVENT, this.handlePreloadFailure);
    if (!this.state.hasError) scheduleStableBrowserRecoveryReset();
  }

  componentWillUnmount() {
    window.removeEventListener(BROWSER_RECOVERY_EVENT, this.handlePreloadFailure);
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const classification = classifyBrowserRecoveryError(error);
    return {
      hasError: true,
      isChunkError: classification.kind === "chunk" || classification.kind === "css-preload",
      isOffline: classification.offline,
      recoverySuppressed: hasBrowserRecoveryAttempted(),
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const classification = classifyBrowserRecoveryError(error);
    const isChunk =
      classification.kind === "chunk" || classification.kind === "css-preload";
    const willReload = isChunk && !classification.offline && recoverFromChunkError();
    if (isChunk && !willReload) markBrowserRecoverySuppressed();

    // Report before reloading. `reloadSuppressed` lets the server distinguish a
    // transient post-deploy stale shell (we reload to recover → logged as warn,
    // does not page) from a broken deploy where the reload didn't help (logged
    // as error → still counts toward the per-hour alert threshold).
    this.reportError(error, classification, info, isChunk && !willReload);
  }

  /**
   * Best-effort report of the render error to the server so production-only
   * failures are visible in the system event log. Never throws and never
   * blocks the UI — the fallback still renders regardless.
   */
  private reportError(
    error: unknown,
    classification: ReturnType<typeof classifyBrowserRecoveryError>,
    info: ErrorInfo,
    reloadSuppressed = false,
  ) {
    try {
      const diagnostics = getClientRuntimeDiagnostics();
      const originalStack =
        error instanceof Error
          ? error.stack || ""
          : error && typeof error === "object" && typeof (error as { stack?: unknown }).stack === "string"
            ? (error as { stack: string }).stack
            : "";
      const online =
        typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
          ? navigator.onLine
          : classification.offline
            ? false
            : null;
      const componentStack = sanitizeTelemetryText(info?.componentStack, 4000);
      const payload = JSON.stringify({
        source: "client-error-boundary",
        message: sanitizeTelemetryText(classification.message),
        // Keep the original JS stack (after the shared redactor), rather than
        // replacing it with the recovery category. This is the primary
        // evidence needed to distinguish app/library/bridge incidents.
        stack: sanitizeTelemetryText(originalStack, 4000),
        componentStack,
        url:
          typeof window !== "undefined"
            ? sanitizeTelemetryUrl(`${window.location.origin}${window.location.pathname}`)
            : "",
        attribution: inferClientErrorAttribution(originalStack, componentStack),
        ...(diagnostics.clientBuildId ? { clientBuildId: diagnostics.clientBuildId } : {}),
        ...(diagnostics.browserFamily ? { browserFamily: diagnostics.browserFamily } : {}),
        ...(diagnostics.browserMajor !== undefined ? { browserMajor: diagnostics.browserMajor } : {}),
        supportsArrayAt: diagnostics.supportsArrayAt,
        supportsStringAt: diagnostics.supportsStringAt,
        online,
        recoveryKind: classification.kind,
        isChunkError: classification.kind === "chunk" || classification.kind === "css-preload",
        reloadSuppressed,
      });
      const postViaFetch = () => {
        if (typeof fetch !== "function") return;
        void fetch("/api/client-errors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      };
      // sendBeacon can return false (queue full / payload too large); fall back
      // to fetch(keepalive) so the report isn't silently dropped.
      const sent =
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function" &&
        navigator.sendBeacon(
          "/api/client-errors",
          new Blob([payload], { type: "application/json" }),
        );
      if (!sent) postViaFetch();
    } catch {
      /* reporting must never break the boundary */
    }
  }

  private handleReload = () => {
    clearBrowserRecoveryAttempt();
    if (this.state.isOffline) {
      forceFreshReload(false);
      return;
    }
    if (!recoverFromChunkError()) forceFreshReload(false);
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Inline styles on purpose: the fallback must not depend on Tailwind /
    // design-system chunks, which may be the very thing that failed to load.
    return (
      <div
        role="alert"
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          background: "#0f1419",
          color: "#f5f5f5",
          fontFamily:
            "Montserrat, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>
          {this.state.isChunkError
            ? this.state.recoverySuppressed
              ? "Recupero automatico non riuscito"
              : "Aggiornamento in corso…"
            : this.state.isOffline
              ? "Connessione assente"
              : "Si è verificato un problema"}
        </h1>
        <p
          style={{
            maxWidth: "28rem",
            color: "#9c9e9f",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {this.state.isChunkError
            ? this.state.recoverySuppressed
              ? "Il recupero automatico è già stato tentato. Ricarica manualmente quando la connessione è stabile; non ricaricheremo ancora da soli."
              : "È disponibile una nuova versione dell'applicazione. Ricarichiamo la pagina per applicarla."
            : this.state.isOffline
              ? "La connessione non è disponibile. Controlla la rete e usa il pulsante per riprovare; non eseguiremo ricariche automatiche."
            : "La pagina non è stata caricata correttamente. Prova a ricaricare; se il problema persiste, contatta l'assistenza."}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          data-testid="button-error-reload"
          style={{
            cursor: "pointer",
            border: "1px solid #e0ae26",
            background: "#e0ae26",
            color: "#0f1419",
            fontWeight: 600,
            padding: "0.625rem 1.25rem",
            borderRadius: "0.375rem",
            fontSize: "0.95rem",
          }}
        >
          Ricarica la pagina
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
